/**
 * 「平台能力」页内容。
 *
 * 本页是宣传重点：能力、取舍、边界都要说清楚，且每一条都能在 `platform/docs/` 里找到出处。
 */
import type { IconName } from './icons';
import type { ActionLink } from './home';
import type { Role as RoleKey } from './roles';

export interface PageIntro {
  eyebrow: string;
  title: string;
  lead: string;
  actions: readonly ActionLink[];
}

export const PLATFORM_INTRO: PageIntro = {
  eyebrow: '平台能力',
  title: '监管的本质，是看得清、管得住、查得到',
  lead: 'Havorix™ 针对无人机运营中最易失控的三个环节：谁在看、谁能动、动过什么。六大模块解决「看得清」，角色与指令分层解决「管得住」，审计记录解决「查得到」。我们相信，监管系统的价值不在于功能数量，而在于每一项状态都真实可信。',
  actions: [
    { label: '进入平台', to: 'https://havorix.work', external: true, variant: 'primary' },
    { label: '查看接入协议', to: '/platform#access', variant: 'secondary' },
  ],
};

export const MODULE_SECTION = {
  eyebrow: '六大模块',
  title: '六个模块，构成一条完整链路',
  text: '这并非六项并列的功能，而是一条链路：先建档，再监管，最终可追溯。',
} as const;

export interface RealtimeStep {
  step: string;
  title: string;
  text: string;
  icon: IconName;
}

export const REALTIME_STEPS: readonly RealtimeStep[] = [
  {
    step: '01',
    icon: 'plug',
    title: '先探活，再建立连接',
    text: '首先请求机体的信息接口，确认设备在线，并顺带完成鉴权与地址校验。该探活接口在任何状态下均须可用，否则故障发生时将无从排查。',
  },
  {
    step: '02',
    icon: 'zap',
    title: '以 WebSocket 推流为主',
    text: '正常情况下，遥测由机体主动推送。浏览器与机体直接建立连接，平台不参与中转。',
  },
  {
    step: '03',
    icon: 'link',
    title: '推流中断则降级为轮询',
    text: '断开后按 1 秒、2 秒、4 秒的间隔退避重连，同时改用 REST 轮询。界面持续更新，仅频率与实时性有所下降。',
  },
  {
    step: '04',
    icon: 'clock',
    title: '超时即判定失联',
    text: '超过设定时长未收到新数据，即按失联处理，并说明原因为超时而非其他因素。链路降级可以接受，掩盖降级不可接受。',
  },
];

export interface RealtimeNote {
  icon: IconName;
  title: string;
  text: string;
}

export const REALTIME_NOTES: readonly RealtimeNote[] = [
  {
    icon: 'key',
    title: '三种凭据模式',
    text: '长期 API Key、平台签发的短期令牌，或依据网络隔离不下发凭据，可按机体实际能力选择。',
  },
  {
    icon: 'signal',
    title: '链路质量仅呈现分级',
    text: '良好、一般、稍弱、很差四档，并设有死区与驻留判定，避免 1 Hz 数据下末位数值持续跳动。',
  },
  {
    icon: 'lock',
    title: '推流同样携带凭据',
    text: '浏览器的 WebSocket 无法自定义请求头，因此令牌通过查询参数传递；其余接口仍走请求头。',
  },
];

export interface ThresholdLevel {
  level: string;
  label: string;
  text: string;
  samples: readonly { name: string; value: string }[];
}

export const THRESHOLD_LEVELS: readonly ThresholdLevel[] = [
  {
    level: 'caution',
    label: '注意',
    text: '尚未进入危险区间，但趋势值得留意。为操作员预留反应时间，无需中断当前作业。',
    samples: [
      { name: '电量', value: '35%' },
      { name: '链路质量', value: '60' },
      { name: '延迟', value: '150 ms' },
    ],
  },
  {
    level: 'warning',
    label: '警告',
    text: '须即刻处理。界面将提升该机体的可见度，使其在机队中明确区分。',
    samples: [
      { name: '电量', value: '25%' },
      { name: '链路质量', value: '40' },
      { name: '延迟', value: '300 ms' },
    ],
  },
  {
    level: 'critical',
    label: '严重',
    text: '已越过安全边界，按立即处置对待。',
    samples: [
      { name: '电量', value: '15%' },
      { name: '链路质量', value: '20' },
      { name: '延迟', value: '600 ms' },
    ],
  },
];

export interface OverrideLayer {
  order: string;
  title: string;
  text: string;
}

export const OVERRIDE_LAYERS: readonly OverrideLayer[] = [
  { order: '01', title: '平台默认值', text: '面向监管场景取保守值：宁可提前告警，不可漏报。' },
  { order: '02', title: '平台设置覆盖', text: '在系统设置中按整个机队统一调整，例如现场网络普遍较差时放宽延迟要求。' },
  { order: '03', title: '单机覆盖', text: '某台机体工况特殊时可单独设置，不影响机队其余成员。' },
];

export const THRESHOLD_NOTE =
  '同一物理量的劣化方向可能相反：电量越低越危险，温度越高越危险。两组方向相反的阈值必须各自保持单调，否则单帧数据即可同时命中三个等级，等级划分也就失去意义。';

export interface PermissionRow {
  role: RoleKey;
  label: string;
  description: string;
  grants: string;
  denied: string;
}

export const PERMISSION_ROWS: readonly PermissionRow[] = [
  {
    role: 'admin',
    label: '平台管理员',
    description: '拥有平台全部权限，可管理用户、设备档案与平台设置。',
    grants: '全部',
    denied: '—',
  },
  {
    role: 'operator',
    label: '操作员',
    description: '可查看机队与全部遥测数据，并下发起飞、降落、返航及航线任务指令。',
    grants: '机队总览、遥测、飞行与航线指令',
    denied: '修改设备档案、管理用户',
  },
  {
    role: 'maintainer',
    label: '维护员',
    description: '可维护设备档案、批量配置与凭据轮换，并执行维护类指令。',
    grants: '设备档案读写、维护指令、审计查看',
    denied: '下发飞行指令、管理用户',
  },
  {
    role: 'viewer',
    label: '观察员',
    description: '仅可查看机队状态与遥测数据，无法下发任何指令。',
    grants: '只读',
    denied: '任何指令与写操作',
  },
];

export interface DefenseLayer {
  order: string;
  title: string;
  text: string;
}

export const DEFENSE_LAYERS: readonly DefenseLayer[] = [
  { order: '01', title: '导航与路由', text: '无权限的模块入口不予渲染，直接输入地址亦无法进入。' },
  { order: '02', title: '界面操作', text: '按钮依权限禁用，并明确提示所缺权限，而非点击后无响应。' },
  { order: '03', title: 'Worker 强制鉴权', text: '每个受保护的接口均经过同一层鉴权。前两层关乎使用体验，这一层才是真正的安全边界。' },
];

export interface SpecTable {
  head: readonly string[];
  rows: readonly (readonly string[])[];
}

export const ENDPOINT_TABLE: SpecTable = {
  head: ['字段', '说明', '示例'],
  rows: [
    ['protocol', 'http 或 https', 'https'],
    ['host', '域名或 IP，不带协议前缀', 'drone-01.example.com'],
    ['port', '端口', '8080'],
    ['basePath', 'REST 根路径', '/api/v1'],
    ['wsPath', '遥测推流路径', '/api/v1/telemetry/stream'],
  ],
};

export const CREDENTIAL_TABLE: SpecTable = {
  head: ['模式', '凭据形式', '适用场景'],
  rows: [
    ['apiKey', 'X-Api-Key 请求头', '机体侧可长期安全保存密钥'],
    ['platformToken', 'Authorization 请求头 + 推流查询参数', '凭据需短期有效、可随时吊销'],
    ['none', '不下发凭据', '机体按网络层隔离，例如仅在专网内可达'],
  ],
};

export const STATUS_TABLE: SpecTable = {
  head: ['状态码', 'code', '平台的处理方式'],
  rows: [
    ['401 / 403', 'unauthorized', '判定凭据被拒，提示更换接入凭据'],
    ['404 / 405', 'not_found / method_not_allowed', '判定地址或方法不匹配，指向端点配置'],
    ['422', 'invalid_payload', '按字段逐条回显参数问题'],
    ['429', 'rate_limited', '退避后重试，避免对机体造成过载'],
    ['5xx', 'drone_offline / injected_failure / internal_error', '进入链路降级流程，并在界面上明确说明'],
  ],
};

export const ACCESS_NOTE =
  '平台仅依据 HTTP 状态码进行分支判断。错误体中的 code 供日志与排障使用，message 则是会原样呈现给操作员的中文短句。因此接入方应确保状态码准确，而非让文案承担判断职责。';

export interface DeployItem {
  icon: IconName;
  title: string;
  text: string;
}

export const DEPLOY_ITEMS: readonly DeployItem[] = [
  {
    icon: 'cpu',
    title: 'Worker 承载控制面',
    text: '接口、鉴权与审计均运行于 Worker，按请求次数计费，空闲时段成本接近于零。',
  },
  {
    icon: 'database',
    title: 'D1 保存结构化数据',
    text: '用户、设备档案、平台设置与审计日志均存储于此，建表脚本与种子数据随代码库一并管理。',
  },
  {
    icon: 'layers',
    title: '前端为静态站点',
    text: 'React + Vite 的构建产物托管于 Pages，可随时绑定至贵方自有域名。',
  },
  {
    icon: 'globe',
    title: '顶点域名经边缘代理',
    text: '机体与 API 分属不同子域时，由一层轻量反向代理统一入口，免去跨域与证书的反复配置。',
  },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const FAQ: readonly FaqItem[] = [
  {
    q: '尚未配备真机，能否先行试用？',
    a: '可以。平台内置 6 台模拟机体，注册、总览、单机管理、指令下发与告警的完整流程均可跑通，并可主动注入断链与慢响应以验证降级逻辑。',
  },
  {
    q: '平台是否会保存我方飞行数据？',
    a: '不会。遥测由浏览器直连机体获取，不经过平台。平台保存的是账号、设备档案、平台设置与操作记录，不保存位置与航迹。',
  },
  {
    q: '是否必须使用贵方机体？',
    a: '不必。平台仅依赖一份接口约定，任何满足该约定的机体均可接入。我们不销售无人机整机。',
  },
  {
    q: '能否部署到我方自有的云环境？',
    a: '可以。平台本体是一套可独立部署的应用，可部署至贵方自有账号与域名，数据与账号体系完全由贵方掌握。',
  },
  {
    q: '机体是否必须可被浏览器访问？',
    a: '是。这是「浏览器直连机体」架构的必然代价。若机体仅在专网内可达，通常需配合边缘代理或专线路由方案，此项可一并商议。',
  },
];
